/**
 * LinkTaskToMatterPanel — putting a task on a matter, in a side panel.
 *
 * The only way to do this used to be a bare `Dropdown` half-way down the
 * Details tab, next to an "Update Matter" button: no search across a firm's
 * matters, no sight of what the task is already on, and invisible from
 * anywhere else on the page. The task header's three-dots menu now offers
 * "Link to matter" and it opens this.
 *
 * Modelled on `ConnectToProjectPanel` deliberately: linking a thing to a
 * container is ONE interaction in this app, and a task should not invent a
 * third shape for it. Both halves are on screen — what it is linked to, and
 * what it can be linked to (searchable).
 *
 * ONE MATTER AT A TIME, and no unlink. A task carries a single `matter_uuid`,
 * so picking a different matter MOVES the task rather than adding a second
 * link; and no endpoint clears the field, so offering "Unlink" would be
 * offering something the backend cannot do.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCheckLight, PiSuitcaseSimpleLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import { updateTask } from '../../store/actions/TasksActions';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import Badge from '../shared/TailwindComponents/Badge';
import { Well } from '../shared/brand/Field';

export interface LinkTaskToMatterPanelProps {
  taskUuid: string;
  /** The matter the task is on already, when it is on one. */
  currentMatter?: { uuid?: string; title?: string } | null;
  /** Called after a successful link so the page behind refreshes. */
  onChanged?: () => void;
  onClose: () => void;
}

const LinkTaskToMatterPanel: React.FC<LinkTaskToMatterPanelProps> = ({
  taskUuid,
  currentMatter,
  onChanged,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [matters, setMatters] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string>('');

  const load = useCallback(async () => {
    const response = await dispatch(fetchMatters({ page: 1, page_size: 100 }) as any);
    setMatters(Array.isArray(response?.matters) ? response.matters : []);
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const matterId = (matter: any): string =>
    String(matter.uuid || matter.id || matter.MatterID || '');
  const matterTitle = (matter: any): string =>
    matter.title || matter.MatterName || t('Untitled Matter');

  const results = useMemo(() => {
    // The matter it is already on is not a candidate for linking to itself.
    const available = matters.filter((matter) => matterId(matter) !== currentMatter?.uuid);
    const needle = query.trim().toLowerCase();
    if (!needle) return available;
    return available.filter((matter) =>
      `${matterTitle(matter)} ${matter.client?.name || ''} ${matter.description || ''}`
        .toLowerCase()
        .includes(needle)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matters, query, currentMatter?.uuid]);

  const handleLink = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      const result = await dispatch(updateTask(taskUuid, { matter_uuid: selected }) as any);
      // A falsy result means the thunk already surfaced the error toast.
      if (!result) return;
      onChanged?.();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Skeleton variant="list" rows={6} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-5 py-4 space-y-6">
        {/* ── Already linked ── */}
        <section>
          <h4 className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-2">
            {t('Linked matter')}
          </h4>
          {currentMatter?.uuid ? (
            <Well className="flex items-center gap-3 px-3 py-2">
              <PiSuitcaseSimpleLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
              <p className="min-w-0 flex-1 text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {currentMatter.title || t('Untitled Matter')}
              </p>
              <Badge color="gray">{t('Current')}</Badge>
            </Well>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t('Not linked to a matter yet.')}
            </p>
          )}
        </section>

        {/* ── Available to link ── */}
        <section>
          <h4 className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-2">
            {currentMatter?.uuid ? t('Move to another matter') : t('Link to a matter')}
          </h4>

          <SearchInput
            wrapperClassName="w-full mb-3"
            size="small"
            value={query}
            onChange={setQuery}
            placeholder={t('Search matters')}
          />

          {results.length === 0 ? (
            <EmptyState
              icon={<PiSuitcaseSimpleLight />}
              headline={query ? t('No matters match your search') : t('No matters available')}
              subtext={
                query
                  ? t('Try a different search term.')
                  : t('Create a matter first, then link this task to it.')
              }
            />
          ) : (
            <div className="space-y-2">
              {results.map((matter: any) => {
                const uuid = matterId(matter);
                const isSelected = selected === uuid;
                return (
                  <button
                    key={uuid}
                    type="button"
                    onClick={() => setSelected(isSelected ? '' : uuid)}
                    aria-pressed={isSelected}
                    className={clsx(
                      'w-full text-left flex items-start gap-3 rounded-md border px-3 py-2',
                      'transition-colors duration-200 ease-slide',
                      isSelected
                        ? 'border-champagne/60 bg-champagne/10'
                        : 'border-hairline-soft bg-surface-sunken/70 hover:border-champagne/40'
                    )}
                  >
                    <span
                      className={clsx(
                        'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
                        isSelected ? 'border-champagne bg-champagne text-ink' : 'border-hairline'
                      )}
                    >
                      {isSelected && <PiCheckLight className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {matterTitle(matter)}
                      </span>
                      {matter.client?.name && (
                        <span className="block text-xs text-fg-muted truncate">
                          {matter.client.name}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="flex-shrink-0 flex items-center justify-end gap-2 border-t border-hairline-soft px-5 py-3">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          disabled={!selected || isSaving}
          isLoading={isSaving}
          onClick={handleLink}
        >
          {currentMatter?.uuid ? t('Move task') : t('Link matter')}
        </Button>
      </div>
    </div>
  );
};

export default LinkTaskToMatterPanel;
